import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Alert,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SubscriptionPlanService from "../../../services/SubscriptionPlanService";

const TIER_COLORS = {
  FREE: "default",
  BASIC: "info",
  PREMIUM: "secondary",
  ENTERPRISE: "success",
};

/** ROOT_ADMIN list of subscription plans. */
const SubscriptionPlansList = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = () => {
    setLoading(true);
    SubscriptionPlanService.listPlans()
      .then((data) => {
        setPlans(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load subscription plans");
        setLoading(false);
      });
  };

  const employeeLimit = (plan) => {
    const ent = (plan.entitlements || []).find((e) => e.featureKey === "EMPLOYEE_COUNT");
    if (!ent) return "—";
    return ent.unlimited ? "Unlimited" : ent.limitValue;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5">Subscription Plans</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate("create")}>
              New Plan
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Employee Limit</TableCell>
                    <TableCell>Durations</TableCell>
                    <TableCell>Public</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plans.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No plans yet.</TableCell>
                    </TableRow>
                  )}
                  {plans.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>{plan.name}</TableCell>
                      <TableCell>
                        <Chip label={plan.tier} size="small" color={TIER_COLORS[plan.tier] || "default"} />
                      </TableCell>
                      <TableCell>{employeeLimit(plan)}</TableCell>
                      <TableCell>{(plan.prices || []).length}</TableCell>
                      <TableCell>{plan.isPubliclyAvailable ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.active ? "Active" : "Inactive"}
                          size="small"
                          color={plan.active ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => navigate(`edit/${plan.id}`)}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubscriptionPlansList;
